import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Banner } from '@osd/ui/banner';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { LoadingState } from '@osd/ui/loading-state';
import { Skeleton, SkeletonCircle, SkeletonText } from '@osd/ui/skeleton';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/loading-state — live docs for the `LoadingState` composite.
 *
 * LoadingState maps the query tri-state (loading / error / ready)
 * onto one render call. Without it, every consumer writes the same
 * `if (loading) return …; if (error) return …; return data` ladder.
 */
export const Route = createFileRoute('/ui/_layout/loading-state')({
  component: LoadingStatePage,
});

function LoadingStatePage() {
  return (
    <Stack gap={6}>
      <Section
        title="LoadingState"
        hint="Composite that maps the query tri-state (loading / error / ready) onto one render call"
      >
        <Text>
          Without LoadingState, every consumer writes the same{' '}
          <code>if (loading) return …; if (error) return …;</code> ladder. This collapses the three
          branches into one prop set. The wrapper renders <code>role=&quot;region&quot;</code> +
          <code>aria-busy=&quot;true&quot;</code> on the loading branch.
        </Text>
      </Section>

      <Section title="Live demo" hint="Toggle between loading / error / ready">
        <Demo />
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Basic"
            code={`<LoadingState
  loading={users.isLoading}
  error={users.isError ? users.error : null}
  onRetry={() => void users.refetch()}
>
  <UserList users={users.data!.users} />
</LoadingState>`}
          />
          <CodeBlock
            language="tsx"
            title="Custom loadingFallback (skeleton)"
            code={`<LoadingState
  loading={users.isLoading}
  loadingFallback={
    <Stack gap={3}>
      <SkeletonCircle size={48} />
      <SkeletonText lines={3} />
    </Stack>
  }
  error={users.isError ? users.error : null}
  onRetry={() => void users.refetch()}
>
  <UserList users={users.data} />
</LoadingState>`}
          />
          <CodeBlock
            language="tsx"
            title="Custom errorFallback"
            code={`<LoadingState
  error={users.error}
  errorFallback={({ error, retry }) => (
    <Card>
      <Card.Body>
        <Text variant="danger">Couldn\u2019t load users: {error.message}</Text>
        {retry && <Button onClick={retry}>Try again</Button>}
      </Card.Body>
    </Card>
  )}
>
  <UserList users={users.data} />
</LoadingState>`}
          />
        </Stack>
      </Section>

      <Banner variant="info" title="Priority order">
        When both <code>loading</code> and <code>error</code> are set, error wins. This matches
        TanStack Query&rsquo;s own
        <code>isError</code> semantics (an errored query is no longer &ldquo;loading&rdquo;).
      </Banner>
    </Stack>
  );
}

function Demo(): JSX.Element {
  const [mode, setMode] = useState<'loading' | 'error' | 'ready'>('loading');
  return (
    <Stack gap={3}>
      <Stack direction="row" gap={2}>
        <Button
          variant={mode === 'loading' ? 'primary' : 'default'}
          onClick={() => setMode('loading')}
        >
          Loading
        </Button>
        <Button variant={mode === 'error' ? 'primary' : 'default'} onClick={() => setMode('error')}>
          Error
        </Button>
        <Button variant={mode === 'ready' ? 'primary' : 'default'} onClick={() => setMode('ready')}>
          Ready
        </Button>
      </Stack>
      <Card>
        <Card.Body>
          <LoadingState
            loading={mode === 'loading'}
            error={mode === 'error' ? new Error('Network request timed out') : null}
            onRetry={() => setMode('loading')}
            loadingFallback={
              <Stack direction="row" gap={3} align="center">
                <SkeletonCircle size={48} />
                <Stack gap={2} className="osd-flex-1">
                  <Skeleton width="40%" height="1.25rem" />
                  <SkeletonText lines={2} />
                </Stack>
              </Stack>
            }
          >
            <Stack direction="row" gap={3} align="center">
              <SkeletonCircle size={48} />
              <Stack gap={1} className="osd-flex-1">
                <Text>Alice Example</Text>
                <Text muted>alice@example.com</Text>
              </Stack>
            </Stack>
          </LoadingState>
        </Card.Body>
      </Card>
    </Stack>
  );
}
